import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { appErrorMonitorService } from '../services/appErrorMonitorService';
import { telemetryMonitorService } from '../services/telemetryMonitorService';
import {
  aggregateMonitoringIssues,
  monitoringIssueFromAppError,
  monitoringIssueFromTelemetry,
  type MonitoringIssueGroup,
  type MonitoringIssueLevel,
} from '../utils/performanceIssueSolutions';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.55rem 0.75rem;
`;

const ToolbarCluster = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
`;

const ToolbarSpacer = styled.div`
  flex: 1;
  min-width: 0.5rem;
`;

const ToolbarMeta = styled.span`
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.9rem;
  border-radius: 10px;
  border: 0;
  background: var(--primary);
  color: var(--onPrimary);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.85rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--surfaceVariant);
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.45rem 0.65rem;
  font-size: 0.82rem;
`;

const SearchInput = styled.input`
  min-width: 220px;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.45rem 0.65rem;
  font-size: 0.82rem;
`;

const Feedback = styled.div<{ $tone: 'error' | 'warning' | 'info' }>`
  border-radius: 12px;
  padding: 0.65rem 0.85rem;
  font-size: 0.82rem;
  color: ${({ $tone }) =>
    $tone === 'error' ? 'var(--error)' : $tone === 'warning' ? 'var(--warning)' : 'var(--info)'};
  background: color-mix(
    in srgb,
    ${({ $tone }) =>
        $tone === 'error' ? 'var(--error)' : $tone === 'warning' ? 'var(--warning)' : 'var(--info)'}
      12%,
    var(--surface)
  );
  border: 1px solid
    color-mix(
      in srgb,
      ${({ $tone }) =>
          $tone === 'error' ? 'var(--error)' : $tone === 'warning' ? 'var(--warning)' : 'var(--info)'}
        28%,
      var(--outline)
    );
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatTile = styled.div`
  border-radius: 14px;
  border: 1px solid var(--outline);
  background: var(--surface);
  padding: 0.85rem 0.95rem;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StatValue = styled.div<{ $bad?: boolean }>`
  margin-top: 0.25rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: ${({ $bad }) => ($bad ? 'var(--error)' : 'var(--onSurface)')};
`;

const IssueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const IssueCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-radius: 16px;
  border: 1px solid var(--outline);
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--surfaceVariant) 55%, var(--surface)) 0%,
    var(--surface) 48%
  );
  padding: 1rem 1.05rem;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.06);
`;

const IssueHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const IssueTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  flex: 1;
`;

const IssueTitle = styled.h3`
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.35;
  color: var(--onSurface);
`;

const IssueMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.55rem;
  font-size: 0.78rem;
  color: var(--onSurfaceVariant);
`;

const LevelBadge = styled.span<{ $level: MonitoringIssueLevel }>`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ $level }) => ($level === 'error' ? 'var(--onError)' : 'var(--onWarning)')};
  background: ${({ $level }) => ($level === 'error' ? 'var(--error)' : 'var(--warning)')};
`;

const SolutionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--primary);
  background: color-mix(in srgb, var(--primary) 12%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--primary) 24%, var(--outline));
`;

const PromptShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const PromptToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const PromptLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PromptTextarea = styled.textarea`
  width: 100%;
  min-height: 220px;
  resize: vertical;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: color-mix(in srgb, var(--surfaceVariant) 35%, var(--surface));
  color: var(--onSurface);
  padding: 0.75rem 0.85rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 0.78rem;
  line-height: 1.45;
`;

const EmptyState = styled.div`
  border-radius: 14px;
  border: 1px dashed var(--outline);
  padding: 1.5rem;
  text-align: center;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
`;

type LevelFilter = 'all' | MonitoringIssueLevel;
type FeedbackState = { tone: 'error' | 'warning' | 'info'; text: string } | null;

const formatList = (values: string[]) => (values.length ? values.join(', ') : 'n/a');

const copyText = async (value: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const IssuePromptCard: React.FC<{
  group: MonitoringIssueGroup;
  onCopied: (message: string) => void;
}> = ({ group, onCopied }) => {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');

  const handleCopy = async () => {
    try {
      await copyText(group.cursorPrompt);
      setCopyState('copied');
      onCopied(`Copied Cursor prompt for "${group.title}".`);
      window.setTimeout(() => setCopyState('idle'), 1800);
    } catch {
      setCopyState('error');
      onCopied('Could not copy automatically. Select the prompt text and copy manually.');
    }
  };

  return (
    <IssueCard>
      <IssueHeader>
        <IssueTitleBlock>
          <IssueTitle>{group.title}</IssueTitle>
          <IssueMeta>
            <LevelBadge $level={group.level}>{group.level}</LevelBadge>
            <SolutionBadge>{group.solutionTitle}</SolutionBadge>
            <span>{group.count} occurrence{group.count === 1 ? '' : 's'}</span>
            <span>Last seen {new Date(group.lastSeenAt).toLocaleString()}</span>
          </IssueMeta>
        </IssueTitleBlock>
        <GhostButton type="button" onClick={() => void handleCopy()}>
          {copyState === 'copied' ? 'Copied' : copyState === 'error' ? 'Copy failed' : 'Copy prompt'}
        </GhostButton>
      </IssueHeader>

      <IssueMeta>
        <span>Sources: {formatList(group.sources)}</span>
        <span>Platforms: {formatList(group.platforms)}</span>
        <span>Screens: {formatList(group.screenOrPages)}</span>
        {group.actionNames.length ? <span>Actions: {formatList(group.actionNames)}</span> : null}
        {group.edgeFunctions.length ? <span>Edge: {formatList(group.edgeFunctions)}</span> : null}
        {group.eventNames.length ? <span>Events: {formatList(group.eventNames)}</span> : null}
      </IssueMeta>

      <PromptShell>
        <PromptToolbar>
          <PromptLabel>Cursor prompt</PromptLabel>
          <GhostButton type="button" onClick={() => void handleCopy()}>
            Copy
          </GhostButton>
        </PromptToolbar>
        <PromptTextarea readOnly value={group.cursorPrompt} aria-label={`Cursor prompt for ${group.title}`} />
      </PromptShell>
    </IssueCard>
  );
};

export const SuperAdminPerformanceSolutionsTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [rawIssueCount, setRawIssueCount] = useState(0);
  const [groups, setGroups] = useState<MonitoringIssueGroup[]>([]);

  const loadIssues = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFeedback({ tone: 'info', text: 'Loading recent errors and warnings...' });

    try {
      const [appErrorsResult, telemetryResult] = await Promise.all([
        appErrorMonitorService.getRecentSevereEvents(500),
        telemetryMonitorService.getRecentSevereTelemetryEvents(500),
      ]);

      if (!appErrorsResult.success) {
        setError(appErrorsResult.message || 'Failed to load app error events.');
        setFeedback({ tone: 'error', text: appErrorsResult.message || 'Failed to load app error events.' });
        setLoading(false);
        return;
      }

      if (!telemetryResult.success) {
        setError(telemetryResult.message || 'Failed to load telemetry events.');
        setFeedback({ tone: 'error', text: telemetryResult.message || 'Failed to load telemetry events.' });
        setLoading(false);
        return;
      }

      const issues = [
        ...(appErrorsResult.data ?? [])
          .map(monitoringIssueFromAppError)
          .filter((issue): issue is NonNullable<typeof issue> => Boolean(issue)),
        ...(telemetryResult.data ?? [])
          .map(monitoringIssueFromTelemetry)
          .filter((issue): issue is NonNullable<typeof issue> => Boolean(issue)),
      ];

      const aggregated = aggregateMonitoringIssues(issues);
      setRawIssueCount(issues.length);
      setGroups(aggregated);
      setFeedback({
        tone: aggregated.length ? 'info' : 'warning',
        text: aggregated.length
          ? `Grouped ${issues.length} recent events into ${aggregated.length} unique issue${aggregated.length === 1 ? '' : 's'}.`
          : 'No recent errors or warnings found in the last 500 events per source.',
      });
    } catch (loadError: any) {
      setError(loadError?.message || 'Failed to load monitoring issues.');
      setFeedback({ tone: 'error', text: loadError?.message || 'Failed to load monitoring issues.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadIssues();
  }, [loadIssues]);

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    return groups.filter((group) => {
      if (levelFilter !== 'all' && group.level !== levelFilter) return false;
      if (!query) return true;
      const haystack = [
        group.title,
        group.messageSample,
        group.solutionTitle,
        ...group.sources,
        ...group.platforms,
        ...group.screenOrPages,
        ...group.actionNames,
        ...group.edgeFunctions,
        ...group.eventNames,
        group.cursorPrompt,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [groups, levelFilter, search]);

  const stats = useMemo(() => {
    const errors = filteredGroups.filter((group) => group.level === 'error').length;
    const warnings = filteredGroups.filter((group) => group.level === 'warning').length;
    const occurrences = filteredGroups.reduce((sum, group) => sum + group.count, 0);
    return {
      uniqueIssues: filteredGroups.length,
      occurrences,
      errors,
      warnings,
    };
  }, [filteredGroups]);

  return (
    <Stack>
      <Toolbar>
        <ToolbarCluster>
          <PrimaryButton type="button" disabled={loading} onClick={() => void loadIssues()}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </PrimaryButton>
        </ToolbarCluster>
        <ToolbarCluster>
          <Select
            value={levelFilter}
            aria-label="Filter fix prompts by severity"
            onChange={(event) => setLevelFilter(event.target.value as LevelFilter)}
          >
            <option value="all">All severities</option>
            <option value="error">Errors only</option>
            <option value="warning">Warnings only</option>
          </Select>
          <SearchInput
            type="search"
            placeholder="Search issues or prompts"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            aria-label="Search fix prompts"
          />
          <PrimaryButton
            type="button"
            disabled={loading}
            onClick={() => setSearch(searchInput.trim())}
          >
            Search
          </PrimaryButton>
          <GhostButton
            type="button"
            disabled={loading || (!search && !searchInput)}
            onClick={() => {
              setSearch('');
              setSearchInput('');
            }}
          >
            Clear
          </GhostButton>
        </ToolbarCluster>
        <ToolbarSpacer />
        <ToolbarMeta>
          Scans the latest 500 app errors and 500 telemetry warnings/errors, then merges duplicates.
        </ToolbarMeta>
      </Toolbar>

      {feedback && <Feedback $tone={feedback.tone}>{feedback.text}</Feedback>}
      {error && <Feedback $tone="error">{error}</Feedback>}

      <StatsGrid>
        <StatTile>
          <StatLabel>Unique issues</StatLabel>
          <StatValue>{stats.uniqueIssues}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>Total occurrences</StatLabel>
          <StatValue>{stats.occurrences}</StatValue>
          <ToolbarMeta>{rawIssueCount} raw events scanned</ToolbarMeta>
        </StatTile>
        <StatTile>
          <StatLabel>Error groups</StatLabel>
          <StatValue $bad={stats.errors > 0}>{stats.errors}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>Warning groups</StatLabel>
          <StatValue $bad={stats.warnings > 0}>{stats.warnings}</StatValue>
        </StatTile>
      </StatsGrid>

      {filteredGroups.length === 0 && !loading ? (
        <EmptyState>
          {groups.length === 0
            ? 'No errors or warnings to turn into fix prompts yet.'
            : 'No issues match the current filters.'}
        </EmptyState>
      ) : (
        <IssueList>
          {filteredGroups.map((group) => (
            <IssuePromptCard
              key={group.dedupeKey}
              group={group}
              onCopied={(message) => setFeedback({ tone: 'info', text: message })}
            />
          ))}
        </IssueList>
      )}
    </Stack>
  );
};
